import * as React from "react";
import {cx} from "emotion";

import "./logo-grid.scss";

import {cs}               from "@Common/react/chain-services";
import {fragments}        from "@Common/react/fragments";
import {Invoke}           from "@Common/react/invoke";
import {keyed}            from "@Common/react/keyed";
import {scnv}             from "@Common/react/state-change-nested-value";
import {Static2}          from "@Common/react/static-2";
import {UseState}         from "@Common/react/use-state";
import {plural}           from "@Common/ui-components/language/plural";
import {ErrorHighlighter} from "@Common/ui-components/light/error-highlighter";
import {scrollIntoView}   from "@Common/ui-components/light/scroll-into-view";
import {sort}             from "@Common/utils/collections";
import {chain}            from "@Common/utils/fs";
import {reverse}          from "@Common/utils/strings";

import {formatImageSize} from "@Xpresso/routes/account/common/format-image-size";

export const LogoGrid = ({logos, program, getSelectedNum, setSelecteds, next}) => cs(
    ["desire", (_, next) => UseState({next})], // {mode,changeImage}
    ["currentMode", ({imgDirections}, next) => next(chain(
        logos?.filter((logo) => getSelectedNum(logo.printUrl)) ?? [],
        (_) => (
            _.length === 0 ? (
                "none"
            ) : _.length === 2 ? (
                "double"
            ) : (
                "single"
            )
        ),
    ))],
    ["sorted", ({}, next) => next(
        chain(
            logos??[],
            (_) => sort<any>(_, (logo) => getSelectedNum(logo.printUrl) ?? 999),
        )
    )],

    ["requiredActions", ({desire, sorted}, next) => next((() => {
        if (!desire.value) {
            return null;
        }

        if (desire.value.changeImage) {
            return {
                message: "Please select an image to change or cancel",
                selectMore: (url) => {
                    desire.onChange(null);
                    setSelecteds(sorted.filter((logo) => getSelectedNum(logo.printUrl)).map((logo1, i) => i !== desire.value.changeImage-1 ? logo1.printUrl : url));
                },
            };
        }

        const desiredCount = modes.find((m) => m.value === desire.value.mode).count;
        const currentCount = sorted.filter((logo) => getSelectedNum(logo.printUrl)).length;

        // console.log({desiredCount, currentCount});
        if (desiredCount > currentCount) {
            return {
                // message: "Please select " + (desiredCount - currentCount) + " more " + plural("image", desiredCount - currentCount),
                message: (
                    desiredCount === 1 ? (
                        "Please select an image"
                    ) : desiredCount - currentCount === 1 ? (
                        "Please select 1 more image"
                    ) : (
                        "Please select " + (desiredCount - currentCount) + " " + plural("image", desiredCount - currentCount)
                    )
                ),
                selectMore: (url) => {
                    setSelecteds([
                        ...sorted.filter((logo) => getSelectedNum(logo.printUrl)).map((logo) => logo.printUrl),
                        url,
                    ]);
                },
            };
        } else {
            // team vs single
            return {
                message: <>
                    {"Please select an image or"}
                    {" "}
                    <a {...{
                        onClick: () => desire.onChange(null),
                    }}>select single mode</a>
                </>,
                selectMore: (url) => {
                    setSelecteds([
                        url,
                    ]);
                },
            };
        }
    })())],
    ["eh", (_, next) => ErrorHighlighter({next})],
    ["domRef", (_, next) => Static2({next})],
    ["selecteds", ({sorted}, next) => next(sorted.filter((logo) => getSelectedNum(logo.printUrl)))],

    ["syncError", ({desire, currentMode}, next) => next((() => {
        if (desire.value) {
            return null;
        }
        // if (currentMode === "double") {
        //     const selecteds = logos?.filter((logo) => getSelectedNum(logo.printUrl)) ?? [];
        //     const teamImg = selecteds.find((logo) => chain(
        //         imgDirections.value?.[logo.printUrl],
        //         (_) => _ && _.width > _.height,
        //     ));
        //     if (teamImg) {
        //         return {
        //             message: "Please select all portrait images for Double mode",
        //         };
        //     }
        // }
        // return ;
    })())],

    ({desire, currentMode, syncError, requiredActions, sorted, selecteds, eh, domRef}) => next({
        invalid: !!desire.value || syncError,
        errorsHandling: {
            highlightErrors: () => {
                eh.start();
                setTimeout(
                    () => scrollIntoView(domRef.get()),
                    0,
                );
            },
        },
        renderControls: () => cs(
            ["activeMode", (_, next) => next(desire.value?.mode ?? currentMode)],
            ({}, next) => fragments(
                next(),
                desire.value?.mode && desire.value?.mode === currentMode && Invoke({
                    action: () => {
                        desire.onChange(null);
                    },
                }),
                // currentMode === "double" && !program.programPreferences.allowSecondHeadshot && Invoke({
                //     action: () => {
                //         setSelecteds(selecteds.slice(0, 1).map((logo) => logo.printUrl));
                //     },
                // }),
                // currentMode === "team" && !program.programPreferences.allowTeamHeadshot && Invoke({
                //     action: () => {
                //         setSelecteds([]);
                //     },
                // }),
            ),
            ({activeMode}) => (
                <div {...{
                    className: "logo-grid-7jm controls",
                    "xd-qa-type": "card",
                    "xd-qa-name": "modes",
                }}>
                    {[
                        "none",
                        "single",
                    ]
                        .filter(v => v)
                        .map((mode) => cs(
                            keyed(mode),
                            ["active", (_, next) => next(activeMode === mode)],
                            ["label", (_, next) => next(modes.find(({value}) => value === mode).label)],
                            ({active, label}) => (
                                <div {...{
                                    className: cx("mode", {active}),

                                    "xd-qa-type": "button",
                                    "xd-qa-name": label,
                                    ...!active && {
                                        onClick: () => {
                                            desire.onChange({mode});

                                            const modeConfig = modes.find(({value}) => value === mode);
                                            const selecteds = chain(
                                                logos ?? [],
                                                (_) => _.filter((logo) => getSelectedNum(logo.printUrl)),
                                            );

                                            if (selecteds.length > modeConfig.count) {
                                                setSelecteds(chain(
                                                    selecteds,
                                                    (_) => _.map((logo) => logo.printUrl),
                                                    (_) => sort(_, getSelectedNum),
                                                    (_) => _.slice(0, modeConfig.count),
                                                ));
                                            }
                                        },
                                    },
                                }}>
                                    {label}
                                </div>
                            ),
                        ))
                    }

                </div>
            ),
        ),
        render: ({onEdit, onAdd, onDelete, showErrors,}) => cs(
            ({}, next) => (
                <div className="logo-grid-7jm grid">
                    {next()}
                </div>
            ),
            () => <>

                {requiredActions?.message || syncError ? (
                    showErrors && (
                        <div className={cx("error", eh.css)} ref={domRef.set}>
                            <div className="left">
                                <i className="fa fa-exclamation-triangle"/>
                            </div>
                            <div className="right">
                                {(requiredActions ?? syncError).message}
                            </div>
                        </div>
                    )
                ) : !selecteds.length && (
                    <div {...{
                        className: "not-selected",

                        "xd-qa-type": "card",
                        "xd-qa-name": "No Logo selected",
                    }}>
                        <div className="left">
                            <i {...{
                                className: "fa fa-info-circle",

                                "xd-qa-type": "icon",
                                "xd-qa-name": "Info Icon",
                            }}/>
                        </div>
                        <div className="right">
                            <div {...{
                                className: "title",
                                "xd-qa-type": "label",
                                "xd-qa-name": "No Logo selected",
                            }}>
                                No Logo Selected
                            </div>
                            <div {...{
                                className: "message",
                                "xd-qa-type": "text",
                                "xd-qa-name": "No Logo selected",
                            }}>
                                Areas marked for Logos in the proof, will be empty in the final product
                            </div>
                        </div>
                    </div>
                )}

                <div className="selected">
                    {/*{console.log("logos", logos)}*/}
                    {selecteds.map((logo) => cs(
                        keyed(logo.id),
                        ["selectedNum", (_, next) => next(getSelectedNum(logo.printUrl))],
                        ({selectedNum}) => LogoCard({
                            logo,
                            onEdit,
                            onDelete,
                            // imageName,
                            selectedNum,
                            ...desire.value?.changeImage ? {
                                ...selectedNum === desire.value.changeImage ? {
                                    onCancel: () => desire.onChange(null),
                                } : {
                                    onSwitch: () => {
                                        desire.onChange(null);
                                        setSelecteds(reverse(selecteds).map((logo) => logo.printUrl));
                                    },
                                },
                            } : {
                                onChange: () => desire.onChange({changeImage: selectedNum}),
                            },
                            // ...!imgDirections.value?.[logo.printUrl] && {
                            //     onImgSize: (size) => scnv(imgDirections, [logo.printUrl], () => size),
                            // },
                        }),
                    ))}
                </div>

                {requiredActions?.selectMore && (
                    <div className="unselected">
                        {!logos.length && (
                            <div className="no-data">
                                <div className="message">
                                    <div {...{
                                        className: "line",
                                        "xd-qa-type": "text",
                                        "xd-qa-name": "Error message - No logo",
                                    }}>
                                        You do not have any logos in your profile
                                    </div>
                                    <div className="line">
                                        Please upload one (JPEG, PNG or PDF)
                                    </div>
                                </div>
                                {!!onAdd && (
                                    <button {...{
                                        onClick: onAdd,

                                        "xd-qa-type": "button",
                                        "xd-qa-name": "Add Headshot",

                                    }}>ADD LOGO</button>
                                )}
                            </div>
                        )}

                        {sorted.filter((logo) => !getSelectedNum(logo.printUrl)).map((logo) => cs(
                            keyed(logo.id),
                            ({}) => LogoCard({
                                logo,
                                onEdit,
                                onDelete,

                                makeAction: {
                                    action: () => requiredActions.selectMore(logo.printUrl),
                                },
                            }),
                        ))}
                    </div>
                )}
            </>,
        ),
    }),
);


const LogoCard = (
    {
        logo,
        imageName,
        onEdit,
        onDelete,
        selectedNum,
        onChange,
        makeAction,
        onCancel,
        onSwitch,
        // onImgSize,
    }
) => (
    <div {...{
        className: cx("logo-card-5r3", {gray: onCancel}),

        "xd-qa-type": "card",
        "xd-qa-name": "Headshot " + logo.name + "." + logo.fileType,
    }}>
        <div className="image">
            <img {...{
                className: "image",
                src: logo.thumbnailUrl,
                alt: "Headshot Image",
                "xd-qa-type": "image",
                "xd-qa-name": "Headshot",
                // ...onImgSize && {
                //     onLoad: (e) => {
                //         const img = e.target;
                //         onImgSize({
                //             width: img.naturalWidth,
                //             height: img.naturalHeight,
                //         });
                //     },
                // },
            }}/>
            {selectedNum && (
                <div {...{
                    className: "label",
                    "xd-qa-type": "label",
                    "xd-qa-name": "Default Headshot",
                }}>
                    Logo {selectedNum}
                </div>
            )}
        </div>

        <div className="right">

            <div className="info">
                <div className="line1">

                    <div {...{
                        className: "file-name",

                        "xd-qa-type": "text",
                        "xd-qa-name": "Headshot Name",
                    }}>
                        {logo.name + "." + logo.fileType}
                    </div>

                    {/* NO logo.quality */}
                    {/*<div className="status">*/}
                    {/*    <div {...{*/}
                    {/*        className: cx("badge", {*/}
                    {/*            high  : logo.quality === "High",*/}
                    {/*            medium: logo.quality === "Medium",*/}
                    {/*            low   : logo.quality === "Low",*/}
                    {/*        }),*/}
                    {/*        "xd-qa-type": "text",*/}
                    {/*        "xd-qa-name": "Headshot Quality",*/}
                    {/*    }}>*/}
                    {/*        {logo.quality}*/}
                    {/*    </div>*/}
                    {/*</div>*/}
                </div>
                <div {...{
                    className: "file-size",
                    "xd-qa-type": "text",
                    "xd-qa-name": "Headshot File Size",
                }}>
                    {formatImageSize(logo.fileSize)}
                </div>
                <div className="actions">
                    {onEdit && (
                        <a {...{
                            onClick: () => onEdit(logo),
                            "xd-qa-type": "link",
                            "xd-qa-name": "Edit",
                        }}>
                            Edit
                        </a>
                    )}

                    {onDelete && (
                        <a {...{
                            onClick: () => onDelete(logo),
                            "xd-qa-type": "link",
                            "xd-qa-name": "Delete",
                        }}>
                            Delete
                        </a>
                    )}
                </div>
            </div>

            <div className="controls">
                {onCancel && <>
                    <button {...{
                        className: cx({disabled: true}),

                        "xd-qa-type": "button",
                        "xd-qa-name": "Change Headshot",
                    }}>
                        Change Logo
                    </button>
                    <a {...{
                        onClick: onCancel,
                        "xd-qa-type": "button",
                        "xd-qa-name": "Cancel",
                    }}>
                        Cancel
                    </a>
                </>}
                {onChange && (
                    <button {...{
                        onClick: onChange,
                        "xd-qa-type": "button",
                        "xd-qa-name": "Change Headshot",
                    }}>
                        Change Logo
                    </button>
                )}
                {onSwitch && (
                    <button {...{
                        onClick: onSwitch,
                        "xd-qa-type": "button",
                        "xd-qa-name": "Switch Headshots",
                    }}>Switch Logos</button>
                )}
                {makeAction && (
                    <button {...{
                        onClick: makeAction.action,
                        className: cx({disabled: makeAction.disabled}),
                        "xd-qa-type": "button",
                        "xd-qa-name": "Make Headshot",
                    }}>
                        Make Logo
                    </button>
                )}
            </div>

        </div>

    </div>
);


const modes = [
    {
        label: "None",
        value: "none",
        count: 0,
    },
    {
        label: "Single",
        value: "single",
        count: 1,
    },
    {
        label: "Double",
        value: "double",
        count: 2,
    },
    // {
    //     label: "Team",
    //     value: "team",
    //     count: 1,
    // },
];


